import React from "react"
import { compressedImageFilter, addStoryblokImageServiceWebpSupport, useCompressedImage } from "./helpers";

///
//Props
//image: Expects a Storyblok image object
//width: If set, the CDN will return an image with this width
//height: If set, the CDN will return an image with this height
//sizes: Expects an object where each key is a breakpoint range and the value is
// the dimensions returned within that breakpoint range (ex. { '-992':'200x200', '992-1152':'400x400', '1152-':'800x800'})
//
//If only width or only height is set, the unset dimension will be determined based on the aspect ratio.
// If width and height are both set it will scale to that size and then crop to preserve as much of the image as possible
// If a focal point is set, it will do the crop so that the focal point is in the center of the crop
// as much as is possible.
// See https://www.storyblok.com/docs/image-service for the documentation of the CDN
//
///
function StoryblokImageBasic({ image, width, height, sizes, withClass }) {
  if(!image) {
    return (<span>INVALID STORYBLOK IMAGE</span>)
  }

  if(sizes) {
    return buildSrcSet(sizes, image, width, height, image ? image.alt : "", withClass)
  }
  
  const data = GetImageUrlAndTagSize(image, width, height);
  return (
    <picture className={withClass}>
      <img src={data.url} alt={image ? image.alt : null} width={data.width || null} height={data.height || null} loading="lazy" />
    </picture>
  )
}

function GetImageUrlAndTagSize(image, width, height) {
  let imageProperties = getImagePropertiesFromURL(image.filename);
  const nativeWidth = imageProperties ? imageProperties.metadata.dimensions.width : null; //image properties can be false, so need to check for that
  const nativeHeight = imageProperties ? imageProperties.metadata.dimensions.height : null;

  //Make sure desired aspect ratio is returned
  let aspectRatio = width && height ? width / height : null;
  if (!aspectRatio && nativeWidth && nativeHeight) {
    //Use native aspect ratio if width and/or height are not specified
    aspectRatio = nativeWidth / nativeHeight;
  }

  let newWidth = nativeWidth;
  let newHeight = nativeHeight;

  let scaleSize = '';
  if ((nativeWidth !== null && nativeHeight !== null) || (width || height)) {
    //If width or height is specified, use them. 
    //If only one is specified, use 0 to auto size the other dimension
    if (width || height) {
      newWidth = width ? width : 0;
      newHeight = height ? height : 0;
    }
    //Ensure the image that comes back is not larger than the native resolution
    //Make sure it conforms to the specified aspect ratio if width and height were specified
    //Use native aspect ratio otherwise
    if (width > nativeWidth || height > nativeHeight) {
      if (aspectRatio >= 1) {
        newWidth = nativeWidth;
        newHeight = Math.floor(newWidth * (1 / aspectRatio));
      } else {
        newHeight = nativeHeight;
        newWidth = Math.floor(newHeight * aspectRatio);
      }
    }
    scaleSize = `${newWidth}x${newHeight}/`;
  }
  //image.focus comes to us in the proper format for the CDN (ex. 100x75:101x76)
  const focalPoint = image.focus ? image.focus : "";

  let newUrl = addStoryblokImageServiceWebpSupport(image.filename);
  const focalFilter = focalPoint ? `focal(${focalPoint}):` : '';
  const adjustments = `filters:${compressedImageFilter}${focalFilter}`;
  newUrl += adjustments;

  return {
    url: newUrl,
    width: newWidth,
    height: newHeight,
    adjustments: adjustments,
    filename: image.filename,
  };
}



function buildSrcSet(sizes, image, width, height, alt, withClass) {
  let url = addStoryblokImageServiceWebpSupport(image.filename)
  let props = getImagePropertiesFromURL(url);

  // Get width and height from props above or default to the dimensions from the image url
  let defaultWidth = width ? width : props? props.metadata.dimensions.width : "100%";
  let defaultHeight = height ? height : props ? props.metadata.dimensions.height : "auto";


  return BuildPictureSourceWithSizes(sizes, image, url, defaultWidth, defaultHeight, alt, withClass)
}

function BuildPictureSourceWithSizes(sizes, image, url, defaultWidth, defaultHeight, alt, withClass) {
  return (
    <picture className={withClass}>
      {Object.keys(sizes).map((breakpoint) => {
        var width = sizes[breakpoint].split("x")[0];
        var height = sizes[breakpoint].split("x")[1];

        if (breakpoint.includes("-")) {
          var minMax = breakpoint.split("-");
          var min = minMax[0];
          var max = minMax[1];

          const imageResult = GetImageUrlAndTagSize(image, width, height);
          let media;
          if (min && max) {
            media = `(min-width: ${min}px) and (max-width: ${max}px)`;
          } else if (min && !max) {
            media = `(min-width: ${min}px)`;
          } else if (!min && max) {
            media = `(max-width: ${max}px)`;
          }
          return (
            <source
              media={media}
              srcSet={imageResult.url}
              key={breakpoint + ":" + sizes[breakpoint]}
              width={imageResult.width ? imageResult.width : null}
              height={imageResult.height ? imageResult.height : null}
            />
          );
        }
        return null;
      })}
      <img
        src={useCompressedImage(url)}
        alt={alt || null}
        width={defaultWidth || null}
        height={defaultHeight || null}
        loading="lazy"
      />
    </picture>
  );
}

///
/// Helper Functions
///

///
// Takes an image url and pulls out the image properties
///
function getImagePropertiesFromURL(image) {
  let url = image;
  const validImageUrlPattern =
    /^(https?:)?\/\/a.storyblok.com\/f\/*[0-9]+\/*[0-9]*x*[0-9]*\/[A-Za-z0-9]+\/[\S]+\.[a-zA-Z]+/;
  const storyblokRegex = /^(https?:)?\/\/a.storyblok.com\//;
  url = validImageUrlPattern.test(url) ? url : "";

  if (!url) {
    return false;
  }

  let originalPath = url.replace(storyblokRegex, "");

  let [, , dimensions, , filename] = originalPath.split("/");
  let [width, height] = dimensions.split("x").map((num) => parseInt(num, 10));
  let [, extension] = filename.split(".");

  let aspectRatio = width / height;
  let metadata = { dimensions: { width, height, aspectRatio } };

  return {
    originalPath,
    extension,
    metadata,
  };
}

///
// This function takes a string of breakpoints and outputs an object that Storyblok Image Basic is expecting
///
function ParseBreakpointsFromString(sizes) {
  var bps = sizes
  var bpsObj = {};
  if (sizes !== null && sizes !== "") {
    var arr = bps.split("\n");
    arr.map((bp) => {
      var regex = /px/gm;
      if (bp.includes("px")) {
        bp = bp.replace(regex, "");
      }
      var bpslt = bp.trim().split(":");
      let key = bpslt[0].trim();
      let val = bpslt[bpslt.length - 1].trim();
      return (bpsObj[key] = val);
    });
    return bpsObj;
  }

  return null;
}

export { 
  GetImageUrlAndTagSize,
  ParseBreakpointsFromString
}
export default StoryblokImageBasic
